import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Checkbox, Field, Input, PageHeader, Spinner } from '../components/ui';
import { Dialog } from '../components/dialogs/Dialog';
import { GooglePlayReconcile } from '../components/GooglePlayReconcile';
import { CacheAPI } from '../services/endpoints/cache';
import { useDispatchAction } from '../hooks/useDispatchAction';
import type { CacheStatusEntry } from '../types/api';
import { relativeTime } from '../utils/format';
import './CachePage.css';

const FALLBACK: CacheStatusEntry[] = [
  { key: 'dashboard',        label: 'Dashboard',        requiresCollection: false },
  { key: 'enriched-media',   label: 'Enriched media',   requiresCollection: true },
  { key: 'playback-media',   label: 'Playback media',   requiresCollection: true },
  { key: 'system-messages',  label: 'System messages',  requiresCollection: false },
];

export function CachePage() {
  const [entries, setEntries] = useState<CacheStatusEntry[]>(FALLBACK);
  const [usingFallback, setUsingFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [dialogKey, setDialogKey] = useState<string | null>(null);
  const [clearAllOpen, setClearAllOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [collectionName, setCollectionName] = useState('');
  const [deleteMessages, setDeleteMessages] = useState(false);
  const [clearAllConfirm, setClearAllConfirm] = useState('');
  const { run } = useDispatchAction();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await CacheAPI.status();
      if (data && data.length) {
        setEntries(data);
        setUsingFallback(false);
      }
    } catch {
      setEntries(FALLBACK);
      setUsingFallback(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const current = entries.find(e => e.key === dialogKey);

  async function performClear(key: string, reasonText: string, coll?: string, purge?: boolean) {
    setBusyKey(key);
    const label = purge ? `Clear ${key} cache & delete messages` : `Clear ${key} cache`;
    await run(label, async () => {
      // The admin endpoint now performs the real flush server-side (and audits
      // it), so call it directly and let failures surface rather than silently
      // falling back to the unaudited legacy endpoints.
      await CacheAPI.clear(key, reasonText, coll, purge);
      await refresh();
    });
    setBusyKey(null);
  }

  function openDialog(key: string) {
    setDialogKey(key);
    setReason('');
    setCollectionName('');
    setDeleteMessages(false);
  }

  async function submit() {
    if (!current || !reason.trim()) return;
    if (current.requiresCollection && !collectionName.trim()) return;
    const purge = current.key === 'system-messages' && deleteMessages;
    await performClear(current.key, reason.trim(), collectionName.trim() || undefined, purge);
    setDialogKey(null);
  }

  async function clearAll() {
    if (clearAllConfirm !== 'CLEAR') return;
    for (const e of entries) {
      if (!e.requiresCollection) {
        await performClear(e.key, 'Clear all (admin)');
      }
    }
    setClearAllOpen(false);
    setClearAllConfirm('');
  }

  return (
    <div>
      <PageHeader
        eyebrow="09 — CACHE"
        title={<>The <em>flush room</em>.</>}
        lede="Each invalidation writes to the audit log. Provide a reason. Collection-bound caches need a collection name. Clear All is restricted to the unbound caches."
        actions={
          <>
            <Button onClick={refresh} disabled={loading}>{loading ? <Spinner /> : 'Refresh'}</Button>
            <Button tone="danger" onClick={() => setClearAllOpen(true)}>Clear all (unbound)</Button>
          </>
        }
      />

      {usingFallback && (
        <Card style={{ marginBottom: 24 }}>
          <div className="nb-cache__notice">
            <strong>Status endpoint unavailable.</strong> Falling back to legacy <code>/api/.../cache/clear</code> routes — these still work but won't show last-cleared timestamps until <code>/api/admin/cache/status</code> ships.
          </div>
        </Card>
      )}

      <div className="nb-cache__grid">
        {entries.map(e => (
          <Card key={e.key} padded={false}>
            <div className="nb-cache__card">
              <div className="nb-cache__head">
                <div>
                  <div className="nb-cache__eyebrow">CACHE · {e.key.toUpperCase()}</div>
                  <div className="nb-cache__name">{e.label}</div>
                </div>
                {e.requiresCollection && <span className="nb-cache__tag">Collection-bound</span>}
              </div>
              <dl className="nb-cache__meta">
                <div>
                  <dt>Last cleared</dt>
                  <dd>{e.lastClearedAt ? relativeTime(e.lastClearedAt) : '—'}</dd>
                </div>
                <div>
                  <dt>By</dt>
                  <dd>{e.lastClearedBy || '—'}</dd>
                </div>
                {e.size !== undefined && (
                  <div>
                    <dt>Size</dt>
                    <dd>{e.size.toLocaleString()} entries</dd>
                  </div>
                )}
              </dl>
              <div className="nb-cache__action">
                <Button
                  tone="ember"
                  onClick={() => openDialog(e.key)}
                  disabled={busyKey === e.key}
                >
                  {busyKey === e.key ? <Spinner /> : 'Clear'}
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <GooglePlayReconcile />

      <Dialog
        open={!!dialogKey}
        onClose={() => setDialogKey(null)}
        title={`Clear ${current?.label || ''} cache`}
        footer={
          <>
            <Button onClick={() => setDialogKey(null)}>Cancel</Button>
            <Button
              tone={deleteMessages && current?.key === 'system-messages' ? 'danger' : 'ember'}
              onClick={submit}
              disabled={!reason.trim() || (current?.requiresCollection && !collectionName.trim())}
            >
              {deleteMessages && current?.key === 'system-messages' ? 'Clear cache & delete messages' : 'Clear cache'}
            </Button>
          </>
        }
      >
        <p className="nb-prose-italic">
          This will invalidate the cache and force the next read to re-build. The reason is recorded in the audit log.
        </p>
        {current?.requiresCollection && (
          <Field label="Collection name" hint="Required for this cache.">
            <Input value={collectionName} onChange={e => setCollectionName(e.target.value)} placeholder="e.g. OGBE" />
          </Field>
        )}
        <Field label="Reason">
          <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Why are you clearing this?" />
        </Field>
        {current?.key === 'system-messages' && (
          <Checkbox
            tone="danger"
            checked={deleteMessages}
            onChange={setDeleteMessages}
            label="Also delete the stored system messages from the database"
            hint="Permanently removes the universal (all-audience) broadcast records that back this cache. This cannot be undone."
          />
        )}
      </Dialog>

      <Dialog
        open={clearAllOpen}
        onClose={() => { setClearAllOpen(false); setClearAllConfirm(''); }}
        title="Clear all unbound caches"
        footer={
          <>
            <Button onClick={() => { setClearAllOpen(false); setClearAllConfirm(''); }}>Cancel</Button>
            <Button tone="danger" disabled={clearAllConfirm !== 'CLEAR'} onClick={clearAll}>
              Clear all
            </Button>
          </>
        }
      >
        <p className="nb-prose-italic">
          This will clear every cache that doesn't require a collection name. Type <code>CLEAR</code> to confirm.
        </p>
        <Field label="Confirm">
          <Input value={clearAllConfirm} onChange={e => setClearAllConfirm(e.target.value)} placeholder="CLEAR" />
        </Field>
      </Dialog>
    </div>
  );
}
